import React, { useEffect } from 'react'
import {
  Form,
  Button,
  Typography,
  Row,
  Col,
  Input,
  DatePicker,
  Checkbox,
  Select,
} from 'antd'
import { useTranslation } from 'react-i18next'
import styles from '../Organizations.module.less'
import { useAppDispatch, useAppSelector } from '~/Store/hooks'
import { getDefaultOrganization } from '~/Store/User/selectors'
import { OrganizationActions } from '~/Store/Organization'
import { PasswordInput } from '~/Components/Forms'
import { GroupActions, selectGroupState } from '~/Store/Groups'
import dayjs from 'dayjs'

const { Title } = Typography

interface Props {
  onSubmit: (values: any) => void
  onCancel: () => void
  initialData?: any | null
  isLoading?: boolean
}

export const CreateUpdateStaffForm: React.FC<Props> = ({
  onSubmit,
  onCancel,
  initialData,
  isLoading = false,
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const dispatch = useAppDispatch()
  const roles = useAppSelector(state => state.organization.roles)
  const defaultOrg = useAppSelector(getDefaultOrganization)
  const { groupData } = useAppSelector(selectGroupState)

  useEffect(() => {
    dispatch(GroupActions.getGroupList({ orgId: defaultOrg?.guid }))
  }, [dispatch])

  // Set initial values when editing
  useEffect(() => {
    if (!initialData) return

    const roleNames = initialData.organizationRoles || []
    const roleIds = roleNames
      .map((roleName: string) => {
        const role = roles.find(r => r.name === roleName)
        return role?.id
      })
      .filter(Boolean)

    form.setFieldsValue({
      // userCode: initialData.userCode,
      fullName:
        `${initialData.lastname || ''} ${initialData.firstname || ''}`.trim(),
      email: initialData.email,
      phone: initialData.phoneNumber,
      organizationJobId: initialData.organizationJobId,
      position: initialData.profile?.position,
      note: initialData.profile?.note,
      isActive: initialData.isActive ?? true,
      checkInDate:
        initialData.checkInDate &&
        initialData.checkInDate !== '0001-01-01T00:00:00'
          ? dayjs(initialData.checkInDate)
          : null,
      lockoutEnabled: initialData.lockoutEnabled ?? false,
      roles: roleIds,
    })
  }, [initialData, roles])

  useEffect(() => {
    if (initialData) return
    if (roles.length && !form.getFieldValue('roles')) {
      form.setFieldsValue({
        roles: [roles[0].id],
        checkInDate: dayjs(),
      })
    }
  }, [roles, initialData])

  const onFinish = (values: any) => {
    onSubmit && onSubmit(values)
  }

  useEffect(() => {
    if (defaultOrg?.guid) {
      dispatch(OrganizationActions.getOrganizationRoles(defaultOrg.guid))
    }
  }, [dispatch, defaultOrg?.guid])

  return (
    <>
      <Form
        className={styles.CreateUpdateForm}
        form={form}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
      >
        <Row gutter={16}>
          {/* <Col span={12}>
            <Form.Item
              label={t('staff.staffCode')}
              name="userCode"
              rules={[{ required: true, message: '' }]}
            >
              <Input />
            </Form.Item>
          </Col> */}

          <Col span={24}>
            <Form.Item
              label={t('staff.fullName')}
              name="fullName"
              rules={[{ required: true, message: '' }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('staff.email')}
              name="email"
              rules={[
                { type: 'email', message: '' },
                { required: true, message: '' },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('password')}
              name="password"
              rules={[
                {
                  required: !initialData,
                  message: '',
                },
              ]}
            >
              <PasswordInput
                placeholder=""
                onGenerate={password => {
                  form.setFieldsValue({
                    password,
                    'Confirm password': password,
                  })
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label={t('phone')} name="phone">
              <Input />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={t('staff.department')} name="organizationJobId">
              <Select placeholder={t('')} allowClear>
                {groupData?.map(group => (
                  <Select.Option key={group.id} value={group.id}>
                    {group.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          {/* <Col span={12}>
            <Form.Item label={t('staff.position')} name="position">
              <Input />
            </Form.Item>
          </Col> */}

          <Col span={12}>
            <Form.Item label={t('staff.note')} name="note">
              <Input />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={t('staff.checkinDate')} name="checkInDate">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="isActive"
              valuePropName="checked"
              initialValue={true}
            >
              <Checkbox>{t('staff.activate')}</Checkbox>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="lockoutEnabled" valuePropName="checked">
              <Checkbox>{t('staff.accountLocked')}</Checkbox>
            </Form.Item>{' '}
          </Col>
        </Row>
        <div className={styles.RoleBox}>
          <Title level={5}>{t('staff.roles')}</Title>

          <Form.Item name="roles">
            <Checkbox.Group style={{ width: '100%' }}>
              <Row gutter={16}>
                {roles.map((role, index) => (
                  <Col span={6} key={index} style={{ marginBottom: 8 }}>
                    <Checkbox value={role.id}>{role.name}</Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </Form.Item>
        </div>
        <div className={styles.FooterModal} style={{ marginTop: 24 }}>
          <Button onClick={onCancel} disabled={isLoading}>
            {t('cancel')}
          </Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {t('save')}
          </Button>
        </div>
      </Form>
    </>
  )
}
